import { Box, styled } from '@mui/material';

import { isHemeraActiveClient } from '»utils/clientsUtils';

const formButtonTopSpacing = {
  marginTop: 'auto',
  lg: {
    marginTop: '30px',
  },
  xl: {
    marginTop: '70px',
  },
  xxl: {
    marginTop: '90px',
  },
};

export const AuthContainer = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'barHeight' &&
    prop !== 'isLoginOrOnboardingInitialSteps' &&
    prop !== 'isNotStinvestCreateAccountStep' &&
    prop !== 'isOnboardingAdvancedSteps' &&
    prop !== 'isOnboardingDocumentAcceptance' &&
    prop !== 'isContractPreviewEnabled',
})(
  ({
    theme,
    barHeight,
    isLoginOrOnboardingInitialSteps,
    isNotStinvestCreateAccountStep,
    isOnboardingAdvancedSteps,
    isOnboardingDocumentAcceptance,
    isContractPreviewEnabled,
  }) => ({
    backgroundColor:
      isOnboardingDocumentAcceptance && isContractPreviewEnabled
        ? theme.palette.colors.actionBar
        : theme.palette.colors.primaryBackground,
    height: `calc(100vh - ${barHeight}px - 56px)`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: `${barHeight}px`,
    paddingBottom: isLoginOrOnboardingInitialSteps && '16px',
    overflowY: 'auto',
    position: 'relative',

    '.formContainer': {
      height: 'fill-available',
      display: 'flex',
      flexDirection: 'column',
      marginBottom: !isLoginOrOnboardingInitialSteps && '64px',
      overflowY: isOnboardingAdvancedSteps && 'auto',

      '& #fieldContainer': {
        padding: 0,
      },

      '.MuiFormControl-root': {
        marginBottom: '16px',

        '&:has(#otpInput)': {
          marginBottom: 0,
        },
        '& .MuiFormGroup-root': {
          flexDirection: 'row',
          justifyContent: 'space-between',
        },

        [theme.breakpoints.up('lg')]: {
          marginBottom: '24px',

          '&:not(.datePickerContainer)': {
            '&:has(.MuiTextField-root)': {
              marginBottom: 0,
            },
          },

          '&:has(#otpInput)': {
            marginBottom: 0,
          },
        },

        '& .MuiFormHelperText-root': {
          marginLeft: 0,
        },

        '&.datePickerFieldContainer': {
          marginBottom: 0,
        },
      },

      '.formButtonsContainer, .submitButton:first-of-type': {
        ...(isLoginOrOnboardingInitialSteps && {
          marginTop: formButtonTopSpacing.marginTop,
        }),

        [theme.breakpoints.up('lg')]: {
          ...(isLoginOrOnboardingInitialSteps && formButtonTopSpacing.lg),
          ...(!isLoginOrOnboardingInitialSteps && {
            justifyContent: 'flex-end',
          }),
        },
        [theme.breakpoints.up('xl')]: {
          ...(isLoginOrOnboardingInitialSteps && formButtonTopSpacing.xl),
        },
        [theme.breakpoints.up('xxl')]: {
          ...(isLoginOrOnboardingInitialSteps && formButtonTopSpacing.xxl),
        },

        '.submitButton, .backButton': {
          width: isLoginOrOnboardingInitialSteps ? '100%' : '148px',

          [theme.breakpoints.up('md')]: {
            width: !isLoginOrOnboardingInitialSteps && '212px',
          },
          [theme.breakpoints.up('lg')]: {
            width: !isLoginOrOnboardingInitialSteps && '148px',
          },
        },

        '.backButton': {
          display: isNotStinvestCreateAccountStep && 'none',
        },
      },

      '.formButtonsContainer': {
        width: '100%',
        justifyContent: 'center',
        paddingBottom: isLoginOrOnboardingInitialSteps && '16px',

        [theme.breakpoints.up('lg')]: {
          width: 'auto',
          paddingBottom: isLoginOrOnboardingInitialSteps && 0,
        },
      },

      '.submitButton:first-of-type': {
        width: '100%',
      },

      '& #divSMSTokenAuth': {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        marginTop: '24px',

        '& #inputFieldContainer': {
          order: 0,

          '& .MuiFormControl-root': {
            marginBottom: '8px',
          },
        },

        '& #sendCodeButton': {
          order: 1,
          fontSize: '12px !important',
          lineHeight: 'normal !important',
          letterSpacing: 'normal',
          color: `${theme.palette.colors.onPrimaryBackground} !important`,
          marginBottom: '64px !important',

          [theme.breakpoints.up('md')]: {
            marginBottom: '80px !important',
          },
        },

        '& #fieldContainer > .MuiInputBase-root': {
          width: '110px',
        },

        [theme.breakpoints.up('lg')]: {
          marginTop: 0,
        },
      },

      [theme.breakpoints.up('lg')]: {
        height: isOnboardingAdvancedSteps ? '579px' : 'auto',
        marginTop: isLoginOrOnboardingInitialSteps ? '24px' : '16px',
        marginBottom: 0,
        overflowY: 'auto',

        /* Firefox-specific styles */
        '@-moz-document url-prefix()': {
          scrollbarColor: `${theme.palette.colors.scrollOnPrimaryBackground} transparent`,
          scrollbarWidth: 'thin',
        },
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
          webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: theme.palette.colors.scrollOnPrimaryBackground,
          borderRadius: '5px',
        },
      },
      [theme.breakpoints.up('xxl')]: {
        height: isOnboardingDocumentAcceptance
          ? '652px'
          : isOnboardingAdvancedSteps && '555px',

        '& .flexWrap': {
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        },
        '&.narrowFields .auto-width .MuiFormControl-root': {
          width: 'auto',
        },
        '&.narrowFields .MuiFormControl-root': {
          width: '288px',

          '&.fullWidth': {
            width: '100%',

            '& .MuiFormGroup-root': {
              justifyContent: 'flex-start',
              gap: '8px',
            },
          },
        },
      },
    },

    [theme.breakpoints.up('lg')]: {
      backgroundColor: theme.palette.colors.primaryBackground,
      flexDirection: 'row',
      alignItems: 'normal',
      marginTop: 0,
      marginBottom: 0,
      height: '100vh',
      paddingBottom: 0,
      overflowY: 'unset',
      position: 'static',
    },
  }),
);

export const OuterFormContainer = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'barHeight' &&
    prop !== 'isOnboardingAdvancedSteps' &&
    prop !== 'isOnboardingDocumentAcceptance' &&
    prop !== 'isContractPreviewEnabled' &&
    prop !== 'hasTopPadding' &&
    prop !== 'formFieldsAreLoaded' &&
    prop !== 'isOnboardingMainFormStep',
})(
  ({
    theme,
    barHeight,
    isOnboardingAdvancedSteps,
    isOnboardingDocumentAcceptance,
    isContractPreviewEnabled,
    hasTopPadding,
    formFieldsAreLoaded,
    isOnboardingMainFormStep,
  }) => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '312px',
    height: '100%',
    color: theme.palette.colors.onPrimaryBackground,
    backgroundColor:
      isOnboardingDocumentAcceptance && isContractPreviewEnabled
        ? theme.palette.colors.actionBar
        : theme.palette.colors.primaryBackground,
    paddingTop: hasTopPadding && '24px',

    [theme.breakpoints.up('md')]: {
      width: '440px',
    },
    [theme.breakpoints.up('lg')]: {
      width: '50%',
      justifyContent: 'center',
      backgroundColor: theme.palette.colors.primaryBackground,
      paddingTop: hasTopPadding && 0,
      marginTop: `${barHeight}px`,
      height: `calc(100vh - ${barHeight}px)`,
      alignItems:
        isOnboardingMainFormStep && !formFieldsAreLoaded && 'flex-start',
    },
    [theme.breakpoints.up('xxl')]: {
      width: isOnboardingAdvancedSteps && 'calc(100vw - 532px - 96px - 96px)',
      justifyContent: isOnboardingAdvancedSteps && 'flex-start',
      marginInline: isOnboardingAdvancedSteps && 'auto',
    },
    [theme.breakpoints.up('xxxxl')]: {
      width: isOnboardingAdvancedSteps && 'calc(100vw - 640px - 112px - 112px)',
    },
  }),
);

export const InnerFormContainer = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'isOnboardingAdvancedSteps' &&
    prop !== 'isOnboardingDocumentAcceptance' &&
    prop !== 'isOnboardingDocumentsAttach',
})(
  ({
    theme,
    isOnboardingAdvancedSteps,
    isOnboardingDocumentAcceptance,
    isOnboardingDocumentsAttach,
  }) => ({
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'static',

    '#divFeedback': {
      position: 'absolute',
      width: '260px',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 9999,

      [theme.breakpoints.up('lg')]: {
        top: isOnboardingAdvancedSteps ? -20 : -45,
      },
      [theme.breakpoints.up('xl')]: {
        top: !isOnboardingAdvancedSteps && -56,
      },
      [theme.breakpoints.up('xxl')]: {
        top: -56,
      },
    },

    '.bem-vindo': {
      fontFamily: isHemeraActiveClient && 'Crimson Text',
    },

    '>.screenDescr:last-child': {
      display: 'none',
    },

    '#screenSummaryContainer': {
      display: isOnboardingDocumentAcceptance && 'none',
      marginTop: !isOnboardingAdvancedSteps && '24px',
      marginBottom: '16px',

      [theme.breakpoints.up('lg')]: {
        marginTop: isOnboardingAdvancedSteps ? '24px' : 0,
        marginBottom: '24px',
      },
    },

    '#onboardingInitialInfoContainer .MuiButton-root': {
      marginTop: formButtonTopSpacing.marginTop,
      marginBottom: '16px',

      [theme.breakpoints.up('lg')]: {
        ...formButtonTopSpacing.lg,
        marginBottom: 0,
      },

      [theme.breakpoints.up('xl')]: {
        ...formButtonTopSpacing.xl,
      },

      [theme.breakpoints.up('xxl')]: {
        ...formButtonTopSpacing.xxl,
      },
    },

    [theme.breakpoints.up('lg')]: {
      width: '340px',
      height:
        !isOnboardingDocumentAcceptance &&
        !isOnboardingDocumentsAttach &&
        'auto',
      maxHeight: '100%',
      paddingBottom: isOnboardingAdvancedSteps && '64px',
      justifyContent: 'center',
      position: 'relative',

      '>.screenDescr': {
        display: 'block',
        textAlign: 'center',
        width: '200px',
        alignSelf: 'center',
        marginTop: '24px',

        ':first-of-type': {
          display: 'none',
        },
        ':last-child': {
          display: !isOnboardingAdvancedSteps && 'block',
          marginBottom: '20px',
        },
      },
    },
    [theme.breakpoints.up('xxl')]: {
      width: isOnboardingAdvancedSteps && '616px',
    },
  }),
);
